import { useCallback } from 'react';

import { useQuery, UseQueryResult } from '@tanstack/react-query';

import {
	buildLabelsExcludedFromDashboard,
	processPlatformConfiguration
} from 'src/apps/dashboard/helpers/ConfigurationProcessingHelpers';
import { getWfaSessions } from 'src/apps/dashboard/services/dashboard/WfaSessionsService';
import { TWfaSessionsResult } from 'src/apps/dashboard/types/WfaDashboardServiceTypes';
import { REFETCH_INTERVAL } from 'src/common/consts/QueryConfigs';
import { useConfiguration } from 'src/common/providers/configurationProvider/ConfigurationProvider';
import { DASHBOARD_WFA_SESSIONS } from 'src/common/providers/reactQueryProvider/QueryKeys';
import { queryClient } from 'src/common/providers/reactQueryProvider/ReactQueryProvider';
import { IUseTableQueryParams } from 'src/common/types/UseTableQueryParamsType';

interface IUseWfaSessionsQueryParams extends IUseTableQueryParams {
	dateRange?: string;
}

export const useWfaSessionsQuery = ({
	dateRange,
	filters,
	sort,
	page,
	pageSize
}: IUseWfaSessionsQueryParams): [UseQueryResult<TWfaSessionsResult>, () => void] => {
	const { configuration } = useConfiguration();
	const { coreLabels } = processPlatformConfiguration(configuration?.PLATFORM);
	const labelsExcludedFromDashboard = buildLabelsExcludedFromDashboard(coreLabels);

	const queryResult = useQuery(
		[DASHBOARD_WFA_SESSIONS, filters, sort, page, pageSize, dateRange, labelsExcludedFromDashboard],
		async (): Promise<TWfaSessionsResult> =>
			getWfaSessions({
				dateRange,
				filterOptions: filters,
				sort,
				page,
				pageSize,
				labelsExcludedFromDashboard
			}),
		{
			keepPreviousData: true,
			refetchInterval: REFETCH_INTERVAL,
			staleTime: REFETCH_INTERVAL
		}
	);

	const invalidate = useCallback(
		() =>
			queryClient.invalidateQueries({ queryKey: [DASHBOARD_WFA_SESSIONS], refetchType: 'none' }),
		[]
	);

	return [queryResult, invalidate];
};
